import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}


function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const StudentLogin: React.FC = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

useEffect(() => {
  console.log('🔍 StudentLogin mounted - initial auth state:', {
    token: localStorage.getItem('authToken'),
    userType: localStorage.getItem('userType'),
    authFlow: localStorage.getItem('authFlow'),
    currentPath: window.location.pathname
  });
}, []);

const handleLoginSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setError(null);
  setLoading(true);

  try {
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    
    await login(email, password);
    
    // ✅ Wait a brief moment for token to be stored
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const storedToken = localStorage.getItem('authToken');
    console.log('🔍 Token after login:', storedToken ? 'EXISTS' : 'MISSING');
    
    if (!storedToken) {
      throw new Error('Login failed: token not stored. Check useAuth hook.');
    }
    
    persistLocalAuthFlow();
    
    console.log('🔍 Post-login auth state:', {
      userType: localStorage.getItem('userType'),
      authFlow: localStorage.getItem('authFlow'),
      token: storedToken ? `${storedToken.substring(0, 20)}...` : 'NULL'
    });
    
    navigate('/student-dashboard');
    
  } catch (err) {
    console.error('🔍 Login error:', err);
    setError(err instanceof Error ? err.message : 'Login failed');
  } finally {
    setLoading(false);
  }
};

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!email || !password || !confirmPassword) {
        throw new Error('All fields are required');
      }
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }
      if (password.length < 8) {
        throw new Error('Password must be at least 8 characters');
      }
      await register(email, password);
      persistLocalAuthFlow();
      // navigate('/student-dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

 const persistLocalAuthFlow = () => {
  try {
    localStorage.setItem('authFlow', 'local');
    localStorage.setItem('userType', 'student'); // ✅ CRITICAL: Add this
    console.log('✅ Auth flow persisted:', {
      authFlow: 'local',
      userType: 'student'
    });
  } catch (err) {
    console.warn('Unable to persist auth flow preference', err);
  }
};

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper elevation={3}>
        <Box sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Skills Tree Rising
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Your portable record of achievement
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="login or register"
              centered
            >
              <Tab label="Login" id="auth-tab-0" aria-controls="auth-tabpanel-0" />
              <Tab label="Register" id="auth-tab-1" aria-controls="auth-tabpanel-1" />
            </Tabs>
          </Box>

          {/* Login Tab */}
          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleLoginSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                placeholder="your.email@example.com"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                placeholder="Enter your password"
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Login'}
              </Button>
            </form>
          </TabPanel>

          {/* Register Tab */}
          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleRegisterSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                placeholder="your.email@example.com"
              />
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                placeholder="Min 8 characters"
                helperText="Minimum 8 characters"
              />
              <TextField
                fullWidth
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                placeholder="Confirm your password"
              />
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                type="submit"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Register'}
              </Button>
            </form>
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default StudentLogin;
